import React, { useState, useEffect, useMemo } from 'react';
import api from '../services/api';

// Ohio Sales Tax Report - Pulls from DEALS table (retail sales with sales_tax_amount > 0)
const SalesTaxReportTab = () => {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());

  useEffect(() => {
    const loadDeals = async () => {
      setLoading(true);
      try {
        const data = await api.getDeals();
        setDeals(data || []);
      } catch (err) {
        console.error('Failed to load deals:', err);
      } finally {
        setLoading(false);
      }
    };
    loadDeals();
  }, []);

  const filteredDeals = useMemo(() => {
    return deals.filter(d => {
      if (d.sale_type !== 'retail') return false;
      if (!d.sales_tax_amount || parseFloat(d.sales_tax_amount) <= 0) return false;
      const saleDate = new Date(d.sale_date);
      return saleDate.getMonth() + 1 === month && saleDate.getFullYear() === year;
    }).sort((a, b) => new Date(a.sale_date) - new Date(b.sale_date));
  }, [deals, month, year]);

  const totalTax = useMemo(() => 
    filteredDeals.reduce((sum, d) => sum + parseFloat(d.sales_tax_amount || 0), 0), 
    [filteredDeals]
  );

  const exportCSV = () => {
    const rows = [['Sale Date', 'Stock #', 'Year/Make/Model', 'VIN', 'Customer Name', 'Sale Price', 'Sales Tax']];
    filteredDeals.forEach(d => {
      rows.push([
        new Date(d.sale_date).toLocaleDateString(),
        d.stock_number || '',
        `${d.year || ''} ${d.make || ''} ${d.model || ''}`.trim(),
        d.vin || '',
        d.buyer_name || '',
        parseFloat(d.sale_price || 0).toFixed(2),
        parseFloat(d.sales_tax_amount || 0).toFixed(2)
      ]);
    });
    rows.push(['', '', '', '', 'TOTAL', '', totalTax.toFixed(2)]);
    const csv = rows.map(r => r.map(c => `"${c}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `ohio-sales-tax-${year}-${String(month).padStart(2, '0')}.csv`;
    a.click();
  };

  const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

  if (loading) return <div className="text-center py-8 text-neutral-500">Loading...</div>;

  return (
    <div>
      <div className="flex gap-3 mb-4 items-center flex-wrap">
        <select value={month} onChange={e => setMonth(parseInt(e.target.value))} className="h-9 px-3 border rounded text-sm">
          {months.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
        </select>
        <select value={year} onChange={e => setYear(parseInt(e.target.value))} className="h-9 px-3 border rounded text-sm">
          {years.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        <button onClick={exportCSV} className="px-3 py-1.5 text-xs bg-green-600 text-white rounded hover:bg-green-700">Export CSV</button>
        <div className="ml-auto text-right">
          <div className="text-xs text-neutral-500">{filteredDeals.length} retail sales</div>
          <div className="font-medium text-lg">Total Tax: <span className="font-mono">${totalTax.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span></div>
        </div>
      </div>
      
      <div className="bg-white border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50">
            <tr>
              <th className="px-3 py-2 text-left font-medium">Sale Date</th>
              <th className="px-3 py-2 text-left font-medium">Stock #</th>
              <th className="px-3 py-2 text-left font-medium">Year/Make/Model</th>
              <th className="px-3 py-2 text-left font-medium">VIN</th>
              <th className="px-3 py-2 text-left font-medium">Customer</th>
              <th className="px-3 py-2 text-right font-medium">Sale Price</th>
              <th className="px-3 py-2 text-right font-medium">Sales Tax</th>
            </tr>
          </thead>
          <tbody>
            {filteredDeals.length === 0 ? (
              <tr><td colSpan={7} className="px-3 py-8 text-center text-neutral-400">No retail sales with sales tax for this period</td></tr>
            ) : (
              filteredDeals.map(d => (
                <tr key={d.id} className="border-t border-neutral-100 hover:bg-neutral-50">
                  <td className="px-3 py-2">{new Date(d.sale_date).toLocaleDateString()}</td>
                  <td className="px-3 py-2 font-medium">{d.stock_number}</td>
                  <td className="px-3 py-2">{d.year} {d.make} {d.model}</td>
                  <td className="px-3 py-2 font-mono text-xs">{d.vin?.slice(-8)}</td>
                  <td className="px-3 py-2">{d.buyer_name}</td>
                  <td className="px-3 py-2 text-right font-mono">${parseFloat(d.sale_price || 0).toLocaleString()}</td>
                  <td className="px-3 py-2 text-right font-mono font-medium text-green-700">${parseFloat(d.sales_tax_amount || 0).toFixed(2)}</td>
                </tr>
              ))
            )}
          </tbody>
          {filteredDeals.length > 0 && (
            <tfoot className="bg-neutral-100 font-medium">
              <tr>
                <td colSpan={6} className="px-3 py-2 text-right">Total Sales Tax:</td>
                <td className="px-3 py-2 text-right font-mono text-green-700">${totalTax.toFixed(2)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
};

export default SalesTaxReportTab;
